import { codeableConcept } from '@seen/core/codeableConcept'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { Button, cn } from '@seen/ui'
import { IconChevronDown } from '@tabler/icons-react'
import type { NodeViewProps } from '@tiptap/react'
import { type JSX, useEffect, useRef, useState } from 'react'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ObservationRichTextEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationRichTextEditor.tsx'
import {
  ObservationNoValue,
  ObservationWrapper,
} from '#src/components/rich-text-editor/ObservationNode/ObservationWrapper.tsx'
import type {
  ObservationNodeAttributes,
  ObservationNodeOptions,
} from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useOptionalParticipantReference } from '#src/contexts/current-participant/useOptionalParticipantReference.ts'
import { useCreateMutation } from '#src/hooks/useCreateMutation.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'

export function ObservationNodeChoiceInput(props: NodeViewProps): JSX.Element {
  const attributes = props.node.attrs as ObservationNodeAttributes
  invariant(
    attributes.scaleType === 'Ord' || attributes.scaleType === 'Nom',
    'scaleType must be Ord or Nom'
  )

  const extensionOptions = props.extension.options as ObservationNodeOptions

  const currentParticipant = useOptionalParticipantReference()
  const participant = extensionOptions.participant ?? currentParticipant

  const practitioner = useCurrentPractitioner()
  const createMutation = useCreateMutation<ExtendedObservation>()

  const answerOptions =
    attributes.answerOptions && attributes.answerOptions.length > 0
      ? attributes.answerOptions
      : ExtendedObservation.getDefaultAnswerOptions(
          attributes.scaleType,
          attributes.codingDisplay
        )

  const comboboxOptions = answerOptions.map((option) => ({
    value: option.code,
    label: option.display,
    keywords: [option.display],
  }))

  const isReadOnly = useIsReadOnly()

  const [loading, setLoading] = useState(false)

  const [note, setNote] = useState('')

  const buttonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (attributes.autofocus && buttonRef.current) {
      buttonRef.current.focus()
      props.updateAttributes({ autofocus: false })
    }
  }, [attributes.autofocus, props])

  const handleSelect = async (value: string): Promise<void> => {
    const answerOption = answerOptions.find((option) => option.code === value)
    invariant(answerOption, 'answerOption is required')
    setLoading(true)
    const createdObservation = await createMutation.mutateAsync(
      ExtendedObservation.fromValueCodeableConcept({
        valueCodeableConcept: codeableConcept(answerOption),
        code: codeableConcept({
          code: attributes.code,
          system: attributes.system,
          display: attributes.codingDisplay,
        }),
        participant,
        practitioner: practitioner.reference,
        note,
      })
    )
    props.updateAttributes({
      reference: createdObservation.reference.reference,
    })
    extensionOptions.onReferencePersisted?.()
    setLoading(false)
  }

  if (!props.editor.isEditable) {
    return <ObservationNoValue label={attributes.codingDisplay} />
  }

  return (
    <ObservationWrapper className="pr-px">
      <span>{attributes.codingDisplay}:&nbsp;</span>
      <Combobox options={comboboxOptions} onSelect={handleSelect}>
        <Button
          ref={buttonRef}
          variant="unstyled"
          size="unstyled"
          disabled={loading || isReadOnly}
          className="ml-px flex items-center gap-1 rounded-[calc(var(--radius)-5px)] bg-card p-0 px-1 hover:bg-gray-460 focus-visible:ring-2 focus-visible:ring-offset-2"
        >
          Select…
          <IconChevronDown className="size-3" />
        </Button>
      </Combobox>
      &nbsp;&nbsp;
      <div
        className="mr-0.75" // Accounts for the shift below.
      />
      <ObservationRichTextEditor
        placeholder="Notes…"
        disabled={isReadOnly}
        onChange={setNote}
        className={cn(
          'grow', // So that the editor can grow to fill the available horizontal space when the content goes onto multiple lines.
          '-ml-0.75' // For visual alignment when the content goes onto multiple lines.
        )}
      />
    </ObservationWrapper>
  )
}
